/**
 * Utility functions for advanced search functionality
 * Extracted from LegislationPage.tsx for better maintainability and testability
 */

/**
 * Helper function to detect advanced search syntax
 * 
 * Detects:
 * - Quoted phrases: "exact match"
 * - Exclusion terms: -word
 * - OR logic: word1 OR word2
 * - Multiple terms: word1 word2
 * 
 * @param query - The search query string
 * @returns True if advanced syntax is detected
 * 
 * @example
 * hasAdvancedSearchSyntax('"housing crisis"') // true
 * hasAdvancedSearchSyntax('housing -education') // true
 * hasAdvancedSearchSyntax('housing OR education') // true
 * hasAdvancedSearchSyntax('simple') // false
 */
export function hasAdvancedSearchSyntax(query: string): boolean {
  if (!query || !query.trim()) return false;
  
  // Check for quotes
  if (/"[^"]+"/.test(query)) return true;
  
  // Check for exclusion terms
  if (/-\s*\w+/.test(query)) return true;
  
  // Check for OR logic
  if (/\s+or\s+/i.test(query)) return true;
  
  // Check for multiple words
  const words = query.replace(/"([^"]+)"/g, ' ').replace(/-\s*\w+/g, ' ').split(/\s+/).filter(Boolean);
  return words.length > 1;
}

import type { LegislationSearchScope } from './legislationUrlState';

/**
 * Get placeholder text for search input based on syntax detection
 *
 * @param query - Current search query
 * @param searchScope - metadata = DB fields; fulltext = indexed bill document text (RAG keyword)
 */
export function getSearchPlaceholder(
  query: string,
  searchScope: LegislationSearchScope = 'metadata'
): string {
  if (searchScope === 'fulltext') {
    return 'Search words inside bill PDFs and versions…';
  }
  if (hasAdvancedSearchSyntax(query)) {
    return "Advanced syntax active! Use quotes, exclusions, etc.";
  }
  return "Search bills by number, title, or content...";
}

/**
 * Get title attribute for search input based on syntax detection
 */
export function getSearchTitle(
  query: string,
  searchScope: LegislationSearchScope = 'metadata'
): string {
  if (searchScope === 'fulltext') {
    return 'Matches bill document text via the search index (not bill number patterns or advanced syntax).';
  }
  if (hasAdvancedSearchSyntax(query)) {
    return "Advanced syntax detected!";
  }
  return 'Try: "housing", "housing -education"; "housing OR education", or "housing education"';
}

